import React,{useState} from 'react';
import {Link} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const {FiCalendar,FiClock,FiMapPin,FiMail,FiCheck,FiInfo}=FiIcons;

const BookingPage=()=> {
  const [formData,setFormData]=useState({
    postcode: '',
    phone: '',
    cleaningType: '',
    frequency: '',
    startDate: '',
    hoursPerWeek: '',
    preferredDay: '',
    preferredTime: '',
    meetCleaner: ''
  });
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [isSubmitted,setIsSubmitted]=useState(false);

  const handleInputChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData for Netlify
      const netlifyFormData=new FormData();
      netlifyFormData.append('form-name','booking');
      Object.keys(formData).forEach(key=> {
        netlifyFormData.append(key,formData[key]);
      });

      const response=await fetch('/',{
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(netlifyFormData).toString()
      });

      if (response.ok) {
        setIsSubmitted(true);
        // Reset form after 5 seconds
        setTimeout(()=> {
          setIsSubmitted(false);
          setFormData({
            postcode: '',
            phone: '',
            cleaningType: '',
            frequency: '',
            startDate: '',
            hoursPerWeek: '',
            preferredDay: '',
            preferredTime: '',
            meetCleaner: ''
          });
        },5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:',error);
      alert('There was an error submitting your booking request. Please try calling us directly at 07878 522378.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculatePrice=()=> {
    if (!formData.hoursPerWeek || !formData.frequency) return 0;
    const hours=parseFloat(formData.hoursPerWeek);
    const hourlyRate=20; // Updated to £20
    
    switch (formData.frequency) {
      case 'weekly': return hours * hourlyRate;
      case 'fortnightly': return hours * hourlyRate;
      case 'monthly': return hours * hourlyRate;
      case 'one-off': return hours * hourlyRate;
      default: return 0;
    }
  };

  const getFrequencyText=()=> {
    switch (formData.frequency) {
      case 'weekly': return 'per week';
      case 'fortnightly': return 'per fortnight';
      case 'monthly': return 'per month';
      case 'one-off': return 'one-off service';
      default: return '';
    }
  };

  // Generate hours options from 2 to 30 hours in 30-minute increments
  const generateHoursOptions=()=> {
    const options=[];
    for (let i=2;i <=30;i +=0.5) {
      const hours=i % 1===0 ? i.toString() : i.toString();
      const display=i % 1===0 ? `${i} hours` : `${Math.floor(i)} hours 30 minutes`;
      options.push(
        <option key={i} value={hours}>
          {display}
        </option>
      );
    }
    return options;
  };

  // Enhanced structured data for booking page
  const bookingPageSchema={
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Book Cleaning Service Online - Diamond Cleaning Aylesbury",
    "description": "Book your professional cleaning service online. Domestic & commercial cleaning in Aylesbury. Get instant quote, flexible scheduling. £20/hour. Call 07878 522378",
    "url": "https://diamondcleaning-aylesbury.com/book",
    "mainEntity": {
      "@type": "Service",
      "name": "Online Booking System",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Diamond Cleaning Aylesbury"
      },
      "offers": {
        "@type": "Offer",
        "price": "20",
        "priceCurrency": "GBP",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "20",
          "priceCurrency": "GBP",
          "unitText": "per hour"
        }
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://diamondcleaning-aylesbury.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Book Service",
          "item": "https://diamondcleaning-aylesbury.com/book"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Book Cleaning Service Online - Diamond Cleaning Aylesbury | Free Quote"
        description="Book your professional cleaning service online. Domestic & commercial cleaning in Aylesbury. Get instant quote, flexible scheduling. £20/hour. Call 07878 522378"
        keywords="book cleaning service online, cleaning quote Aylesbury, online booking cleaning, schedule cleaning service, cleaning appointment Aylesbury, domestic cleaning booking, commercial cleaning quote"
        image="https://diamondcleaning-aylesbury.com/img/domestic-homepage.webp"
        schema={bookingPageSchema}
      />
      <Header currentArea="Aylesbury" />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Book Your Cleaning Service
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get a quote and book your professional cleaning service in just a few minutes
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Details</h2>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Booking Request Submitted!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for your booking request. We'll contact you within 24 hours to confirm your appointment and provide a final quote.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a href="tel:07878522378" 
                         className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold text-center">
                        Call for Immediate Booking: 07878 522378
                      </a>
                    </div>
                  </div>
                ) : (
                  <form name="booking" method="POST" data-netlify="true" onSubmit={handleSubmit} className="space-y-6">
                    {/* Hidden field for Netlify */}
                    <input type="hidden" name="form-name" value="booking" />

                    {/* Postcode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiMapPin} className="inline w-4 h-4 mr-2" />
                        Postcode *
                      </label>
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        placeholder="e.g. HP20 2AB"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    {/* Mobile Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiMail} className="inline w-4 h-4 mr-2" />
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        placeholder="07878 522123"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    {/* Cleaning Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Which type of cleaning do you require? *
                      </label>
                      <select
                        name="cleaningType"
                        value={formData.cleaningType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        <option value="domestic">Domestic Cleaning</option>
                        <option value="commercial">Commercial Cleaning</option>
                      </select>
                    </div>

                    {/* Frequency */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How often do you need our services? *
                      </label>
                      <select
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly (Every 2 weeks)</option>
                        <option value="monthly">Monthly</option>
                        <option value="one-off">One-off service</option>
                      </select>
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiCalendar} className="inline w-4 h-4 mr-2" />
                        When would you like to start? *
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    {/* Hours per service */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiClock} className="inline w-4 h-4 mr-2" />
                        How many hours per service? *
                      </label>
                      <select
                        name="hoursPerWeek"
                        value={formData.hoursPerWeek}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        {generateHoursOptions()}
                      </select>
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-600 mt-0.5" />
                          <p className="text-sm text-blue-800">
                            <strong>Did you know?</strong> Most clients in HP20-2 book 3.5 hours per week
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Preferred Day */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your preferred cleaning day? *
                      </label>
                      <select
                        name="preferredDay"
                        value={formData.preferredDay}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>

                    {/* Preferred Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What time would you prefer? *
                      </label>
                      <select
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        <option value="flexible">Flexible - anytime</option>
                        <option value="morning">Morning - before 12pm</option>
                        <option value="afternoon">Afternoon - after 12pm</option>
                        <option value="evening">Evening - after 4pm to 8pm</option>
                      </select>
                    </div>

                    {/* Meet Cleaner */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Do you want to meet your cleaner first? *
                      </label>
                      <select
                        name="meetCleaner"
                        value={formData.meetCleaner}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Please select...</option>
                        <option value="yes">Yes, I'd like to meet them first</option>
                        <option value="no">No, that's not necessary</option>
                      </select>
                      <div className="mt-2 p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <SafeIcon icon={FiInfo} className="w-4 h-4 text-yellow-600 mt-0.5" />
                          <p className="text-sm text-yellow-800">
                            If you are passing on a set of keys so the cleaner can access your property, you should arrange to meet them first.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-6">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center space-x-2 ${
                          isSubmitting
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white`}
                      >
                        <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                        <span>{isSubmitting ? 'Submitting...' : 'Submit Booking Request'}</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Price Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Your Quote</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Hourly Rate</span>
                    <span className="font-semibold">£20/hour</span>
                  </div>
                  {formData.hoursPerWeek && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Hours per service</span>
                      <span className="font-semibold">{formData.hoursPerWeek}h</span>
                    </div>
                  )}
                  {formData.frequency && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600">Frequency</span>
                      <span className="font-semibold capitalize">{formData.frequency}</span>
                    </div>
                  )}
                </div>

                {calculatePrice() > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        £{calculatePrice().toFixed(2)}
                      </div>
                      <div className="text-sm text-blue-800">
                        {getFrequencyText()}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span>We use our own equipment</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span>We provide cleaning products</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span>Fully insured & vetted cleaners</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                    <span>No contracts or cancellation fees</span>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-4">
                    This is an estimate. Final price will be confirmed after property assessment.
                  </p>
                  <a href="tel:07878522378" 
                     className="block w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-center">
                    Call: 07878 522378
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;